import type { User } from '@qub/base-client';
import { useState, type FormEvent } from 'react';
import type { Profile } from '@/types';
import { Alert, Avatar, Button, Field, Icon, Input, Modal } from './ui';

/** Edit the username (public.profiles) and avatar (storage bucket "avatars"). */
export function ProfileDialog({
  open,
  onClose,
  user,
  profile,
  updateUsername,
  uploadAvatar,
  removeAvatar,
}: {
  open: boolean;
  onClose: () => void;
  user: User;
  profile: Profile;
  updateUsername: (name: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  removeAvatar: () => Promise<void>;
}) {
  const [name, setName] = useState(profile.username);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setSaved(false);
    try {
      await fn();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (await run('name', () => updateUsername(name.trim()))) setSaved(true);
  };

  return (
    <Modal open={open} onClose={onClose} title="Your profile">
      <div className="flex flex-col gap-5">
        {error && <Alert>{error}</Alert>}
        {saved && <Alert tone="info">Saved.</Alert>}
        <div className="flex items-center gap-4">
          <Avatar path={profile.avatar_path} name={profile.username} size={64} />
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-line-strong bg-surface px-3.5 py-2 text-sm font-medium hover:bg-sunken">
              {busy === 'upload' ? <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Icon name="upload" />}
              Upload photo
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="sr-only"
                disabled={busy !== null}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void run('upload', () => uploadAvatar(file));
                }}
              />
            </label>
            {profile.avatar_path && (
              <Button variant="ghost" loading={busy === 'remove'} onClick={() => void run('remove', removeAvatar)}>
                Remove
              </Button>
            )}
          </div>
        </div>
        <p className="-mt-2 text-xs text-muted">PNG, JPEG, WebP or GIF up to 2 MB. Stored privately; shown through signed URLs.</p>

        <form onSubmit={save} className="flex flex-col gap-3">
          <Field label="Username">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required />
          </Field>
          <Button type="submit" variant="primary" loading={busy === 'name'} disabled={!name.trim() || name.trim() === profile.username}>
            Save username
          </Button>
        </form>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg bg-sunken p-3 text-xs">
          <dt className="text-muted">Email</dt>
          <dd className="truncate">{user.email ?? 'Guest (anonymous user)'}</dd>
          <dt className="text-muted">User id</dt>
          <dd className="truncate font-mono">{user.id}</dd>
          <dt className="text-muted">Role</dt>
          <dd className="font-mono">{user.role}</dd>
        </dl>
      </div>
    </Modal>
  );
}
